import React from 'react';

/**
 * Cycles through a list of status texts at a given interval.
 * Returns current text and a boolean indicating if a new text is being revealed.
 */
export function useStatusRotation(
  texts: string[],
  intervalMs: number,
  active: boolean,
): { text: string; revealing: boolean } {
  const [index, setIndex] = React.useState(0);
  const [revealing, setRevealing] = React.useState(false);

  React.useEffect(() => {
    if (!active || !texts.length) return;

    setIndex(0);
    setRevealing(true);

    const revealTimeout = setTimeout(() => setRevealing(false), 500);

    const interval = setInterval(() => {
      setRevealing(true);

      setTimeout(() => {
        // Advance through the texts once and hold on the last one — do NOT wrap,
        // otherwise early messages (e.g. "Scanning competitor platforms...")
        // reappear after the competitors have finished scanning.
        setIndex((prev) => Math.min(prev + 1, texts.length - 1));
        setTimeout(() => setRevealing(false), 500);
      }, 200);
    }, intervalMs);

    return () => {
      clearInterval(interval);
      clearTimeout(revealTimeout);
    };
  }, [texts, intervalMs, active]);

  return {
    text: texts.length > 0 ? texts[index % texts.length] : '',
    revealing,
  };
}
